<script setup lang="ts">
const localePath = useLocalePath();
const { t } = useI18n();

const { checkGiftCardCodeSchema } = useGiftCard();
const businessStore = useBusinessStore();
const {
  booking,
  business,
  selectedLocation,
  selectedEmployee,
  bookingSource,
  goToCheckout,
  selectedPaymentMethod,
  creatingNewBooking,
  tapLoading,
  isNavigating,
  isCardValid,
  finalTotalAfterCoupon,
  invoice,
} = storeToRefs(businessStore);
const route = useRoute();
const globalStore = useGlobalStore();
const { isLoading, progress, isClientBlocked } = storeToRefs(globalStore);
const client = useClient();
const { isLoggedIn } = useAuth();
const { handleNewBooking, prepareFormData } = useCreateBooking();
const { genderQuery } = useServiceGender();
const { navLoading, runWithLoading } = useFooterNav();
const isRescheduling = ref(false);
const isRescheduleFlow = computed(() => route.query.reschedule === "true");

export type ActiveButton =
  | "services"
  | "times"
  | "review-and-confirm"
  | "checkout"
  | "employee-team"
  | "employees";

const props = defineProps<{
  forcedActive?: ActiveButton | null;
  continueHandler?: () => unknown | Promise<unknown>;
}>();

const perServiceSelectionsComplete = computed(() => {
  const n = booking.value.selectedServices.length;
  if (n === 0) return false;
  if (booking.value.bookedServices.length !== n) return false;
  return booking.value.bookedServices.every(
    (s) =>
      s.selected_employee &&
      String(s.selected_employee.id) !== "employee_per_service",
  );
});

/**
 * Every service must hold a time before the booking can move on. The unified
 * times layout starts with nothing selected, so this also guards the
 * reschedule "save" — submitting without a time is a 422.
 */
const hasCompleteTimeSelection = computed(
  () =>
    !!booking.value.bookedServices?.length &&
    booking.value.bookedServices.every((service) => service.selected_time),
);

const activeButton = computed((): ActiveButton | undefined => {
  if (props.forcedActive) return props.forcedActive;
  if (typeof route.name === "string") {
    if (route.name.includes("employees")) return "employees";
    if (route.name.includes("services")) return "services";
    if (route.name.includes("times")) return "times";
    if (route.name.includes("review-and-confirm")) return "review-and-confirm";
    if (route.name.includes("checkout")) return "checkout";
  }
  return undefined;
});

const timesPerServicePath = () =>
  localePath(
    `/${selectedLocation.value?.identifier}/booking/times?source=employee&per_service=1`,
  );

const handleServicesContinue = () => {
  if (bookingSource.value === "per_service") {
    if (!perServiceSelectionsComplete.value) {
      return navigateTo(
        localePath(`/${selectedLocation.value?.identifier}/booking/employees`),
      );
    }
    return navigateTo(timesPerServicePath());
  }
  if (selectedEmployee.value?.id === "employee_per_service") {
    if (!perServiceSelectionsComplete.value) {
      return navigateTo(
        localePath(`/${selectedLocation.value?.identifier}/booking/employees`),
      );
    }
    bookingSource.value = "per_service";
    return navigateTo(timesPerServicePath());
  }
  if (bookingSource.value === "employee" && selectedEmployee.value) {
    return navigateTo(
      localePath(
        `/${selectedLocation.value?.identifier}/booking/times?source=employee&employee_id=${selectedEmployee.value.id}`,
      ),
    );
  }
  return navigateTo(
    localePath(`/${selectedLocation.value?.identifier}/booking/employees`),
  );
};

const handleEmployeesContinue = () => {
  if (!selectedEmployee.value) return;
  if (selectedEmployee.value.id === "employee_per_service") {
    if (!perServiceSelectionsComplete.value) return;
    bookingSource.value = "per_service";
    return navigateTo(timesPerServicePath());
  }
  if (bookingSource.value === "per_service") {
    bookingSource.value = "no_employee";
    booking.value.bookedServices = [];
  }
  return navigateTo(
    localePath(
      `/${selectedLocation.value?.identifier}/booking/times?source=employee&employee_id=${selectedEmployee.value.id}`,
    ),
  );
};

const loginToContinue = () => {
  goToCheckout.value = true;
  globalStore.openSignInModal({
    onSuccess: (c) => {
      if (c?.profile_completed && c?.mobile) {
        runWithLoading(handleConfirm);
      }
    },
  });
};

const completeProfileToContinue = async () => {
  goToCheckout.value = true;
  await navigateTo(localePath(`/complete`));
};

const submitBooking = async () => {
  if (selectedPaymentMethod.value?.key === "tap-cc") {
    const windowCardSDK = ref(window.CardSDK);
    const { tokenize } = windowCardSDK.value;
    tapLoading.value = true;
    await tokenize();
    return;
  } else if (selectedPaymentMethod.value?.key === "gift-card") {
    const { errors } = await checkGiftCardCodeSchema();
    if (errors) {
      return;
    }
  }

  await handleNewBooking();
};

const handleConfirm = async () => {
  const isFreeBooking =
    booking.value.subTotal === 0 ||
    (finalTotalAfterCoupon.value !== null &&
      Number(finalTotalAfterCoupon?.value) === 0);

  if (isFreeBooking) {
    await submitBooking();
    return;
  }

  await navigateTo(
    localePath(`/${selectedLocation.value?.identifier}/booking/checkout`)
  );
};
const displayTotal = computed(() => {
  const summaryTotal = booking.value.summary?.grand_total;
  const isSummaryStep =
    activeButton.value === "review-and-confirm" ||
    activeButton.value === "checkout";

  if (isSummaryStep && summaryTotal !== undefined && summaryTotal !== null) {
    return useFormatAmount(summaryTotal);
  }

  return useFormatAmount(
    finalTotalAfterCoupon.value !== null
      ? finalTotalAfterCoupon.value
      : booking.value.subTotal,
  );
});

const handleReschedule = async () => {
  isRescheduling.value = true;
  try {
    const response = await $larafetch(
      `/api/${useRuntimeConfig().public.apiVersion}/account/${
        selectedLocation.value?.identifier
      }/bookings/${booking.value.id}/reschedule/update`,
      {
        method: "POST",
        query: genderQuery.value,
        body: prepareFormData(),
      }
    );
    useToast().add({
      title: response.message,
      color: "green",
      icon: "i-approved-success",
    });
    await navigateTo(localePath(`/account/bookings`));
  } catch (error) {
    console.log(error);
  } finally {
    isRescheduling.value = false;
  }
};
</script>

<template>
  <CommonFooterShell
    :hide-box1="isRescheduleFlow"
    :label1="t('service_count')"
    :value1="booking.selectedServices?.length"
    :label2="
      isRescheduleFlow ? t('total_after_vat_and_discounts') : t('total')
    "
    :value2="$formatPrice(displayTotal, booking.currency || business?.currency)"
  >
    <template #action>
      <UButton
        v-if="isRescheduleFlow"
        :label="t('label.save')"
        :loading="isRescheduling"
        :disabled="
          !hasCompleteTimeSelection || isLoading || progress || isClientBlocked
        "
        :color="`${selectedLocation?.theme_name}-secondary` as any"
        size="xl"
        class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
        @click="handleReschedule"
      />
      <template v-else>
        <UButton
          v-if="activeButton === 'services'"
          :label="t('label.continue')"
          :color="`${selectedLocation?.theme_name}-secondary` as any"
          size="xl"
          :loading="navLoading"
          :disabled="
            !(booking.selectedServices?.length > 0) || isLoading || progress
          "
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="runWithLoading(handleServicesContinue)"
        />

        <UButton
          v-else-if="activeButton === 'employees'"
          :label="t('label.continue')"
          :color="`${selectedLocation?.theme_name}-secondary` as any"
          size="xl"
          :loading="navLoading"
          :disabled="
            !selectedEmployee ||
            (selectedEmployee?.id === 'employee_per_service' &&
              !perServiceSelectionsComplete) ||
            isLoading ||
            progress
          "
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="runWithLoading(handleEmployeesContinue)"
        />

        <UButton
          v-else-if="activeButton === 'employee-team'"
          :label="t('label.continue')"
          :color="`${selectedLocation?.theme_name}-secondary` as any"
          size="xl"
          :loading="navLoading"
          :disabled="
            !(booking.selectedServices?.length > 0) || isLoading || progress
          "
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="runWithLoading(() => continueHandler?.())"
        />

        <UButton
          v-else-if="activeButton === 'times'"
          :label="t('label.continue')"
          :color="`${selectedLocation?.theme_name}-secondary` as any"
          size="xl"
          :loading="navLoading"
          :disabled="
            !hasCompleteTimeSelection || isLoading || progress || isClientBlocked
          "
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="
            runWithLoading(() =>
              navigateTo(
                localePath(
                  `/${selectedLocation?.identifier}/booking/review-and-confirm`
                )
              )
            )
          "
        />

        <template v-if="activeButton === 'review-and-confirm'">
          <template v-if="isLoggedIn">
            <UButton
              v-if="client?.profile_completed"
              :disabled="isLoading || progress || isClientBlocked"
              :loading="navLoading || creatingNewBooking"
              :label="t('label.next')"
              :color="`${selectedLocation?.theme_name}-secondary` as any"
              size="xl"
              class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
              @click="runWithLoading(handleConfirm)"
            />

            <UButton
              v-else
              :disabled="isLoading || progress"
              :loading="navLoading"
              :label="t('label.complete_profile')"
              :color="`${selectedLocation?.theme_name}-secondary` as any"
              size="xl"
              class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
              @click="runWithLoading(completeProfileToContinue)"
            />
          </template>

          <UButton
            v-else
            :label="t('label.sign_in')"
            :disabled="isLoading || progress"
            :loading="navLoading"
            :color="`${selectedLocation?.theme_name}-secondary` as any"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
            @click="runWithLoading(loginToContinue)"
          />
        </template>

        <UButton
          v-if="activeButton === 'checkout'"
          :label="t('label.checkout')"
          :loading="creatingNewBooking || tapLoading"
          :color="`${selectedLocation?.theme_name}-secondary` as any"
          size="xl"
          :disabled="
            !selectedPaymentMethod?.id ||
            (!isCardValid && selectedPaymentMethod?.key === 'tap-cc') ||
            (!!selectedPaymentMethod?.accept_invoice && !invoice) ||
            isNavigating
          "
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="submitBooking"
        />
      </template>
    </template>
  </CommonFooterShell>
</template>
